import { useState, useEffect } from 'react';
import { Box, Container, Stack, Typography, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import useTable from 'hooks/useTable';
import useNotification from 'hooks/useNotification';
import MuiDataGrid from 'components/MuiDataGrid';

import { FormProps, IPathology } from '../PatientList/api/type';
import PathologyDialog from './PathologyDialog';
import ListActionButtons from './ListActionButtons';
import { formatStringToDate } from 'utils/formatting';
import { updatePathologyHidden } from 'apis/admin';

interface Props extends FormProps {
  defaultList?: IPathology[];
}

const PathologyList = (props: Props) => {
  const { setValue, defaultList } = props;

  const { palette } = useTheme();
  const { onSuccess, onFail } = useNotification();

  const { list, setList } = useTable<IPathology>();
  const [defaultValue, setDefaultValue] = useState<Partial<IPathology> | null>(
    null
  );

  useEffect(() => setList(defaultList ?? []), [defaultList, setList]);
  useEffect(() => {
    setValue(
      'pathology',
      // api 업데이트할 형태로 오브젝트를 저장함
      list.map(item => ({
        ...item,
        pathologyTime: formatStringToDate(item.pathologyTime),
      }))
    );
  }, [list, setValue]);

  const onCreateList = (data: IPathology) => setList(list.concat(data));
  const onUpdateList = (data: IPathology) =>
    setList(
      list.map(item =>
        item.ptPathologyNo === data.ptPathologyNo ? data : item
      )
    );

  const ActionButtons = ({ row }: { row: IPathology }) => {
    const onUpdate = () => setDefaultValue(row);

    const onToggleHidden = async () => {
      const nextHidden = !row.isHidden;

      try {
        await updatePathologyHidden(row.ptPathologyUuid);

        setList(prev =>
          prev.map(item =>
            item.ptPathologyNo === row.ptPathologyNo
              ? { ...item, isHidden: nextHidden }
              : item
          )
        );

        onSuccess(
          nextHidden ? '숨김 처리되었습니다.' : '숨김이 해제되었습니다.'
        );
      } catch (error) {
        console.error('숨김 처리 중 오류 발생:', error);
        onFail(
          nextHidden
            ? '숨김 처리에 실패하였습니다.'
            : '숨김 해제에 실패하였습니다.',
          error
        );
      }
    };

    const onDelete = () =>
      setList(prev =>
        prev.filter(item => item.ptPathologyNo !== row.ptPathologyNo)
      );

    return (
      <ListActionButtons
        onUpdate={onUpdate}
        onDelete={onDelete}
        onToggleHidden={onToggleHidden}
        isHidden={row.isHidden}
      />
    );
  };

  const columns: GridColDef[] = [
    { field: 'ptPathologyNo', headerName: '검사번호' },
    {
      field: 'pathologyTime',
      headerName: '검사시간',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    { field: 'feeId', headerName: '검사코드' },
    { field: 'feeKor', headerName: '검사명(한)' },
    {
      align: 'right',
      field: 'actions',
      headerName: '',
      renderCell: ({ row }) => <ActionButtons row={row} />,
    },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" sx={{ color: `${palette.primary.main}` }}>
            임상병리검사 리스트 (수정가능)
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#555555a4',
              marginLeft: '30px',
            }}
          >
            (추가 시 스마트널스 ENR로 문의해주세요.)
          </Typography>
        </Box>

        <MuiDataGrid
          columns={columns}
          rows={list.map(item => ({ ...item, id: item.ptPathologyNo }))}
        />
      </Stack>

      {defaultValue && (
        <PathologyDialog
          isUpdate={defaultValue.ptPathologyNo ? true : false}
          defaultValue={defaultValue}
          onClose={() => setDefaultValue(null)}
          onSearchNo={(no: number) =>
            list.find(item => item.ptPathologyNo === no) ? true : false
          }
          onSave={defaultValue.ptPathologyNo ? onUpdateList : onCreateList}
        />
      )}
    </Container>
  );
};

export default PathologyList;
